import { useQuery } from 'react-query'
import { DollarSign, TrendingUp, ShoppingCart, Users, Target, Award, BarChart3 } from 'lucide-react'
import { api } from '../services/api'
import Chart from '../components/Chart'

// GOD MODE DASHBOARD WITH REAL TPT DATA - FIXED CALCULATIONS
const Dashboard = () => {
  const { data: dashboardData, isLoading } = useQuery('dashboard', () =>
    api.get('/api/dashboard/kpis').then(res => res.data)
  )

  const { data: trendData, error: trendError } = useQuery('trends', () =>
    api.get('/api/dashboard/trends').then(res => res.data),
    {
      retry: false,
      refetchOnWindowFocus: false
    }
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-400"></div>
      </div>
    )
  }

  // Handle both old and new API formats with SAFE CALCULATIONS
  const kpis = dashboardData?.data?.kpis || dashboardData?.data || {}
  const platforms = dashboardData?.data?.platforms || []
  const topProducts = dashboardData?.data?.topProducts || []

  // SAFE NUMBER CONVERSIONS
  const totalRevenue = Number(kpis.totalRevenue) || 0
  const totalProfit = Number(kpis.totalProfit) || 0
  const totalOrders = Number(kpis.totalOrders) || 0
  const profitMargin = Number(kpis.profitMargin) || 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">Revenue Dashboard</h1>
          <p className="text-xl text-white/80">Your real-time business performance</p>
          <div className="text-sm text-white/60 mt-2">
            Last updated: {new Date().toLocaleString()}
          </div>
        </div>

        {/* KPI Cards - GOD MODE Design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white/80">Total Revenue</p>
                <p className="text-3xl font-bold text-white">${totalRevenue.toLocaleString()}</p>
                <p className="text-sm text-green-400 flex items-center mt-1">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  +12.5%
                </p>
              </div>
              <div className="bg-purple-500/20 p-3 rounded-xl">
                <DollarSign className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white/80">Net Profit</p>
                <p className="text-3xl font-bold text-white">${totalProfit.toLocaleString()}</p>
                <p className="text-sm text-green-400 flex items-center mt-1">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  +8.2%
                </p>
              </div>
              <div className="bg-green-500/20 p-3 rounded-xl">
                <Target className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white/80">Total Orders</p>
                <p className="text-3xl font-bold text-white">{totalOrders.toLocaleString()}</p>
                <p className="text-sm text-green-400 flex items-center mt-1">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  +15.3%
                </p>
              </div>
              <div className="bg-purple-500/20 p-3 rounded-xl">
                <ShoppingCart className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white/80">Profit Margin</p>
                <p className="text-3xl font-bold text-white">{profitMargin.toFixed(1)}%</p>
                <p className="text-sm text-green-400 flex items-center mt-1">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  +2.1%
                </p>
              </div>
              <div className="bg-orange-500/20 p-3 rounded-xl">
                <Award className="w-6 h-6 text-orange-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Revenue Trends</h3>
              <BarChart3 className="w-5 h-5 text-white/60" />
            </div>
            {trendError ? (
              <div className="flex items-center justify-center h-32 text-white/60">
                <div className="text-center">
                  <div className="text-sm">Trends data temporarily unavailable</div>
                  <div className="text-xs mt-1">Dashboard functionality unaffected</div>
                </div>
              </div>
            ) : (
              <Chart
                data={trendData?.data || []}
                xKey="period"
                yKey="revenue"
                color="primary"
              />
            )}
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Platform Performance</h3>
              <Users className="w-5 h-5 text-white/60" />
            </div>
            <div className="space-y-4">
              {platforms.map((platform: any) => (
                <div key={platform.platform} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      platform.platform === 'tpt' ? 'bg-purple-400' : 'bg-gray-400'
                    }`}></div>
                    <span className="font-medium capitalize text-white">{platform.platform}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-white">${(platform.revenue || 0).toLocaleString()}</div>
                    <div className="text-sm text-white/60">{(platform.orders || 0).toLocaleString()} orders</div>
                    <div className="text-xs text-green-400">+{platform.growth || 0}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Products - GOD MODE Table */}
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl">
          <div className="px-6 py-4 border-b border-white/10">
            <h3 className="text-xl font-semibold text-white">Top Performing Products</h3>
            <p className="text-sm text-white/60 mt-1">Your best-selling products from TPT</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Sales
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Earnings
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {topProducts.map((product: any, index: number) => (
                  <tr key={`${product.name}-${product.platform}`} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                            <span className="text-sm font-medium text-purple-400">{index + 1}</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{product.name}</div>
                          <div className="text-sm text-white/60">{product.platform}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 capitalize">
                        {product.platform}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      ${(product.revenue || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {(product.sales || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      ${(product.earnings || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard 